import axios from "axios";
import first from "lodash/first";
import {
  Member,
  Campaign,
  LicenseType,
  OrganizationType,
  PriceType,
  PublishProduct,
} from "../utils/types";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  timeout: 10000,
  headers: {
    "X-Amz-Security-Token": process.env.REACT_APP_API_TOKEN,
  },
});

export async function createCampaign({ id }: { id: string }) {
  try {
    const campaign = await api.post("/campaigns", {
      id: id,
      campaign: id,
      title: "",
      description: "",
    });
    return campaign;
  } catch (e) {
    return null;
  }
}

export async function updateCampaign(id: string, campaign: Campaign) {
  try {
    const result = await api.put(`/campaigns/${id}`, campaign);
    return result;
  } catch (e) {
    return null;
  }
}

export async function getCampaign(id: string): Promise<Campaign | null> {
  try {
    const req = await api.get(`/campaigns/${id}`);
    if (!req.data.Items.length) {
      return null;
    }
    return first(req.data.Items) as Campaign;
  } catch (e) {
    return null;
  }
}

export async function getMember(id: string): Promise<Member | null> {
  try {
    const member = await api.get(`/athletes/${id}`);
    console.log("member", member);
    if (!member.data.Items.length) {
      return null;
    }
    return first(member.data.Items) as Member;
  } catch (e) {
    return null;
  }
}

export const getOrganizationMembers = async (org_sf_id: string) => {
  try {
    const members = await api.get(`/athletes/organization/${org_sf_id}`);
    return members?.data.Items || [];
  } catch (e) {
    console.log(e);
  }
};

export const getMembers = async () => {
  try {
    const members = await api.get(`/athletes`);
    return members?.data.Items || [];
  } catch (e) {
    console.log(e);
  }
};

export const getDesignProductTemplateByOrganization = async (id: string) => {
  try {
    const templates = await api.get(`/product-templates/organization/${id}`);
    return templates.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const getCollections = async () => {
  try {
    const response = await api.get(`/collections`);

    return response?.data || [];
  } catch (e) {
    console.log(e);
  }
};

export const getCollectionsBySalesForceId = async (salesforce_id: string) => {
  try {
    const response = await api.get(`/collections/salesforce/${salesforce_id}`);
    return response?.data?.Items;
  } catch (e) {
    console.log(e);
  }
};

export const getAllCollectionsByIds = async (salesforce_ids: string[]) => {
  try {
    const response = await api.post("/collections/salesforce", {
      ids: salesforce_ids,
    });
    console.log("getAllCollectionsByIds", response);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const getCollectionBySalesForceId = async (salesforce_id: string) => {
  try {
    const response = await api.get(`/collections/salesforce/${salesforce_id}`);
    if (response?.data?.Items.length >= 1) {
      return first(response?.data?.Items);
    } else {
      return [];
    }
  } catch (e) {
    console.log(e);
  }
};

export const generateStoreCollections = async ({
  salesforce_ids,
  stores,
}: {
  salesforce_ids: string[];
  stores: string[];
}) => {
  try {
    let results: any[] = [];
    for (let i = 0; i < stores.length; i++) {
      let ids = [];
      for (let j = 0; j < salesforce_ids.length; j++) {
        ids.push(`${salesforce_ids[j]}-${stores[i]}`);
      }
      const request = await getAllCollectionsByIds(ids);
      const sc = request.collections;
      for (let c = 0; c < sc.length; c++) {
        results.push(sc[c]);
      }
    }
    return results;
  } catch (e) {
    return [];
  }
};

export const checkExistingShopifyCollection = async (
  shopify_collection_id: string,
  store: string
) => {
  try {
    const response = await api.post("/collections/shopify/check", {
      shopify_collection_id: `${shopify_collection_id}`,
      store: store,
    });
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

export const createShopifyCollection = async (
  id: string,
  title: string,
  description: string,
  salesforce_id: string,
  store: string,
  type: string,
  segment: string,
  organization_name: string,
  college_name: string,
  athlete_name: string,
  sports: string,
  account_name: string
) => {
  try {
    const shopifyCollection = await api.post("/collections/shopify", {
      id: id,
      title: title,
      description: description,
      salesforce_id: salesforce_id,
      store: store,
      metafields: [
        {
          key: "type",
          value: [type],
          type: "list.single_line_text_field",
          namespace: "collection",
        },
        {
          key: "segment",
          value: [segment],
          type: "list.single_line_text_field",
          namespace: "collection",
        },
        {
          key: "names",
          value: [organization_name],
          type: "list.single_line_text_field",
          namespace: "organizations",
        },
        {
          key: "college",
          value: [college_name],
          type: "list.single_line_text_field",
          namespace: "athletes",
        },
        {
          key: "names",
          value: [athlete_name],
          type: "list.single_line_text_field",
          namespace: "athletes",
        },
        {
          key: "sports",
          value: [sports],
          type: "list.single_line_text_field",
          namespace: "athletes",
        },
        {
          key: "Name",
          value: [account_name],
          type: "list.single_line_text_field",
          namespace: "Account",
        },
      ],
    });
    const collection = shopifyCollection.data;
    await api
      .post("collections", {
        id: `${salesforce_id}-${store}`,
        shopify_id: `${collection.id}`,
        title: title,
        salesforce_id: salesforce_id,
        description: description,
        handle: collection.handle,
        admin_graphql_api_id: collection.admin_graphql_api_id,
        store: store,
        metafields: [
          {
            key: "type",
            value: [type],
            type: "list.single_line_text_field",
            namespace: "collection",
          },
          {
            key: "segment",
            value: [segment],
            type: "list.single_line_text_field",
            namespace: "collection",
          },
          {
            key: "names",
            value: [organization_name],
            type: "list.single_line_text_field",
            namespace: "organizations",
          },
          {
            key: "college",
            value: [college_name],
            type: "list.single_line_text_field",
            namespace: "athletes",
          },
          {
            key: "names",
            value: [athlete_name],
            type: "list.single_line_text_field",
            namespace: "athletes",
          },
          {
            key: "sports",
            value: [sports],
            type: "list.single_line_text_field",
            namespace: "athletes",
          },
          {
            key: "Name",
            value: [account_name],
            type: "list.single_line_text_field",
            namespace: "Account",
          },
        ],
        created_at: new Date().getTime().toString(),
      })
      .then((res) => {
        return checkExistingShopifyCollection(collection.id, store);
      })
      .catch((error) => {
        console.log("error", error);
        return;
      });
  } catch (error) {
    console.log(error);
  }
};

export async function getOrganization(
  id: string
): Promise<OrganizationType | null> {
  try {
    const response = await api.get(`/organizations/${id}`);
    const items = response.data.Items;
    if (!items.length) {
      return null;
    }
    return first(items) as OrganizationType;
  } catch (e) {
    return null;
  }
}

export const getOrganizationBySalesForceId = async (salesforce_id: string) => {
  try {
    const response = await api.get(
      `/organizations/salesforce/${salesforce_id}`,
      { timeout: 60000 }
    );
    return first(response?.data?.Items);
  } catch (e) {
    console.log(e);
  }
};

export const getCollegeSuggestions = async (searchText = "") => {
  try {
    let organizations: OrganizationType[] = [];

    let result;
    if (searchText === "") {
      result = await api.get(
        `/organizations/search?field=category&size=${25}&value=College`
      );
    } else {
      result = await api.get(
        `/organizations/search?field=full_name&size=${25}&value=${searchText}`
      );
    }
    result?.data?.Items?.forEach(function (doc: OrganizationType) {
      organizations.push({ ...doc });
    });
    return organizations;
  } catch (e) {
    console.log(e);
  }
};

export const getParentSuggestion = async (searchText = "") => {
  try {
    let organizations: OrganizationType[] = [];
    let result;
    if (searchText === "") {
      result = await api.get(
        `/organizations/search?field=full_name&size=${25}`
      );
    } else {
      result = await api.get(
        `/organizations/search?field=full_name&size=${25}&value=${searchText}`
      );
    }
    result?.data?.Items?.forEach(function (doc: OrganizationType) {
      organizations.push({ ...doc });
    });
    return organizations;
  } catch (e) {
    console.log(e);
  }
};

export const getAllNationalOrganizationSuggestions = async (
  category: string
) => {
  let organizations: OrganizationType[] = [];
  let result = await api.get(
    `/organizations/search?field=national&size=${7000}&value=True`
  );
  result?.data?.Items?.forEach((org: OrganizationType) => {
    if (org.category === category) {
      organizations.push(org);
    }
  });
  return organizations as OrganizationType[];
};

export const getAllColleges = async () => {
  try {
    let organizations: OrganizationType[] = [];
    let result = await api.get(
      `/organizations/search?field=menu_name&size=${7000}&value=Colleges`
    );

    result?.data?.Items?.forEach(function (doc: OrganizationType) {
      organizations.push({ ...doc });
    });
    return organizations;
  } catch (e) {
    console.log(e);
  }
};

export const groupedOrganizationsByFullName = async (searchText = "") => {
  try {
    let organizations: OrganizationType[] = [];
    let result;
    if (searchText === "") {
      result = await api.get(
        `/organizations/search?field=full_name&size=${25}`
      );
    } else {
      result = await api.get(
        `/organizations/search?field=full_name&size=${250}&value=${searchText}`
      );
    }
    result?.data?.Items?.forEach(function (doc: OrganizationType) {
      organizations.push({ ...doc });
    });
    let grouped = organizations.reduce(function (acc: any, obj) {
      var key = obj["full_name"];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    return grouped;
  } catch (e) {
    console.log(e);
  }
};

export const getChildSuggestions = async (
  salesforce_ids: string[] = [],
  category: string
) => {
  try {
    if (salesforce_ids.length >= 1) {
      let organizations: OrganizationType[] = [];
      const result = await api.post(`/organizations/parents`, {
        parents: salesforce_ids,
        category: category,
      });
      result?.data?.Items?.forEach(function (doc: OrganizationType) {
        // This checks to make sure results are case sensitve as we loose match salesforce_ids
        const found = salesforce_ids.some((r) => doc?.parents?.includes(r));
        if (found) {
          organizations.push({ ...doc });
        }
      });
      return organizations;
    }
  } catch (e) {
    console.log(e);
  }
};

export const getOrganizations = async () => {
  try {
    const res = await api.get(`/organizations/search?field=full_name&size=25`);
    return res.data.Items;
  } catch (e) {
    return console.log(e);
  }
};

export const getFonts = async () => {
  try {
    const response = await api.get("/fonts");
    return first(response?.data?.Items);
  } catch (e) {
    return e;
  }
};

// LICENSING
export const getLicensing = async () => {
  try {
    const response = await api.get("/licensing");
    return response?.data?.Items || [];
  } catch (e) {
    console.log(e);
  }
};

export const getLicense = async (id: string) => {
  try {
    const response = await api.get(`/licensing/${id}`);

    return first(response?.data?.Items);
  } catch (e) {
    return console.log(e);
  }
};

export const getLicenseByCampaign = async (
  campaign: string,
  next?: { createdAt: string; id: string }
) => {
  try {
    let response;
    if (!next) {
      response = await api.get(`/licensing/campaign/${campaign}`);
    } else {
      response = await api.get(
        `/licensing/campaign/${campaign}?LastEvaluatedKey=${next.id}&createdAt=${next.createdAt}`
      );
    }
    const data = response.data;
    const last = data.LastEvaluatedKey ? data.LastEvaluatedKey.id : null;
    const lastCreated = data.LastEvaluatedKey
      ? data.LastEvaluatedKey.created_at
      : null;
    return {
      next: last,
      createdAt: lastCreated,
      items: data.Items ? data.Items : [],
    };
  } catch (e) {
    return {
      next: null,
      createdAt: null,
      items: [],
    };
  }
};

export const createLicense = async (
  license: Omit<
    LicenseType,
    "id" | "pk" | "affinity_status_id" | "created_at" | "updated_at"
  >
) => {
  try {
    const req = await api.post("/licensing", license);
    return req.data.id;
  } catch (e) {
    return null;
  }
};

export const publishLicenseTask = async (id: any) => {
  try {
    const response = await api.put(`/licensing/${id}`, { status: "start" });
    return first(response?.data?.Items);
  } catch (e) {
    return console.log(e);
  }
};

export const restartLicenseTask = async (id: any) => {
  try {
    const response = await api.put(`/licensing/${id}`, {
      status: "start queue",
    });
    return first(response?.data?.Items);
  } catch (e) {
    return console.log(e);
  }
};

export const createPublishProduct = async (
  product: Omit<
    PublishProduct,
    | "created_at"
    | "updated_at"
    | "id"
    | "shopify_admin_url"
    | "shopify_store_url"
  >
) => {
  try {
    console.log("product", JSON.stringify(product));
    const req = await api.post("/products", product);
    console.log("req", req.data);
    return req.data.id;
  } catch (e) {
    console.log("e", e);
    return;
  }
};

export const createPublishProducts = async (
  products: Omit<
    PublishProduct,
    | "created_at"
    | "updated_at"
    | "id"
    | "shopify_admin_url"
    | "shopify_store_url"
  >[]
) => {
  try {
    let incrementHandles = products.map(function (product, index) {
      return { ...product, handle: product.handle + "_" + index };
    });
    for (let i = 0; i < incrementHandles.length; i++) {
      let product = incrementHandles[i];
      console.log("createPublishProducts", JSON.stringify(product));
      const req = await api.post("/products", { ...product });
      console.log("req", req.data);
    }
    return;
  } catch (e) {
    console.log(e);
    return;
  }
};

export const getPublishingProductsById = async (id: string) => {
  try {
    const response = await api.get(`/products/${id}`);
    return first(response?.data?.Items);
  } catch (e) {
    return e;
  }
};

export const getPublishingProductsByCampaignId = async (
  campaign: string,
  next?: { createdAt: string; id: string }
) => {
  try {
    let response;
    if (!next) {
      response = await api.get(`/products/campaign/${campaign}`);
    } else {
      response = await api.get(
        `/products/campaign/${campaign}?LastEvaluatedKey=${next.id}&createdAt=${next.createdAt}`
      );
    }
    const data = response.data;
    const last = data.LastEvaluatedKey ? data.LastEvaluatedKey.id : null;
    const lastCreated = data.LastEvaluatedKey
      ? data.LastEvaluatedKey.created_at
      : null;
    return {
      next: last,
      createdAt: lastCreated,
      items: data.Items ? data.Items : [],
    };
  } catch (e) {
    return {
      next: null,
      createdAt: null,
      items: [],
    };
  }
};

export const updatePublishProduct = async (product: any) => {
  try {
    const res = await api.put(`/products/${product.id}`, product);
    return res;
  } catch (e) {
    return console.log(e);
  }
};

export const deletePublishingProduct = async (productId: string) => {
  try {
    const res = await api.delete(`/products/${productId}`);
    return res;
  } catch (e) {
    return console.log(e);
  }
};

export const getBulkDownloadCSV = async (query: string) => {
  try {
    const response = await api.get(`/bulk?${query}`);
    return response?.data;
  } catch (e) {
    return e;
  }
};

export const getImportUrl = async (
  key: string,
  content: string,
  bucket: string
) => {
  try {
    if (bucket === "fonts") {
      const response = await api.post(`/fonts/upload`, {
        fileName: key,
        contentType: content,
      });
      return response.data.url;
    }
    if (bucket === "data") {
      const response = await api.post("/bulk", {
        fileName: key,
        contentType: content,
      });
      return response.data.url;
    }
  } catch (e) {
    console.log(e);
  }
};

export const getProductCatalog = async () => {
  try {
    const res = await api.get("/catalog");
    console.log("getProductCatalog", res);
    return res.data.Items;
  } catch (e) {
    return console.log(e);
  }
};

interface CatalogProduct {
  id: string;
  brand: string;
  model: string;
  sku: string;
  description: string;
  affinity_product_category_id: string;
  product_type: string;
  default_vendor: string;
  image_url: string;
}

export async function getCatalogProductBySku(
  sku: string
): Promise<CatalogProduct | null> {
  try {
    const res = await api.get(`/catalog/${sku}`);
    return first(res.data.Items) as CatalogProduct;
  } catch (e) {
    console.log(e);
    return null;
  }
}

export const createCatalogProduct = async (product: CatalogProduct) => {
  try {
    const res = await api.post("/catalog", product);
    return res;
  } catch (e) {
    return console.log("createCatalogProduct error", e);
  }
};

export const updateCatalogProduct = async (product: CatalogProduct) => {
  try {
    const res = await api.put(`/catalog/${product.sku}`, product);
    return res;
  } catch (e) {
    return console.log(e);
  }
};

type ProductImage = {
  id: string;
  brand: string;
  model: string;
  sku: string;
  color: string;
  hex: string;
  color_csv_name: string;
  image_url: string;
  proof_template_name: string;
  view: string;
};

export const createCatalogProductImage = async (image: ProductImage) => {
  try {
    const res = await api.post("/catalog/images", image);
    return console.log(res);
  } catch (e) {
    console.log(e);
  }
};

export const getCatalogProductImagesBySku = async (sku: string) => {
  try {
    const response = await api.get(`/catalog/images/sku/${sku}`);
    return response.data.Items as ProductImage[];
  } catch (e) {
    console.log(e);
  }
};

export const deleteCatalogProductImage = async (id: string) => {
  try {
    const response = await api.delete(`/catalog/images/${id}`);
    return console.log(response);
  } catch (e) {
    console.log(e);
  }
};

export const deleteImagesBySku = async (sku: string) => {
  try {
    let getImagesBySku = await api.get(`/catalog/images/sku/${sku}`);
    let images = getImagesBySku.data.Items;
    for (let i = 0; i < images.length; i++) {
      await api.delete(`/catalog/images/${images[i].id}`).then((res) => {
        console.log("deleted", images[i].id, res);
      });
    }
  } catch (e) {
    console.log(e);
  }
};

export const deleteCatalogProduct = async (sku: string) => {
  try {
    const response = await api.delete(`/catalog/${sku}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

type VendorSku = {
  id: string;
  brand: string;
  model: string;
  sku: string;
  color: string;
  size: string;
  vendor_name: string;
  vendor_sku: string;
};

export const getCatalogProductVendorsBySku = async (sku: string) => {
  try {
    const response = await api.get(`/catalog/vendors/sku/${sku}`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const createCatalogVendorSku = async (product: VendorSku) => {
  try {
    const response = await api.post("/catalog/vendors", product);
    return response;
  } catch (e) {
    console.log(e);
  }
};

export const removeCatalogVendorSku = async (id: string) => {
  try {
    const response = await api.delete(`/catalog/vendors/${id}`);
    return response;
  } catch (e) {
    console.log(e);
  }
};

export const getProofTemplateUploadUrl = async (
  key: string,
  bucket: string,
  contentType: string
) => {
  try {
    const res = await api.post("/catalog/templates/upload", {
      fileName: key,
      bucket: bucket,
      contentType: contentType,
    });
    return res;
  } catch (e) {
    return console.log(e);
  }
};

export const createProofTemplate = async (template: any) => {
  try {
    let create = await api.post(`/catalog/templates`, template);
    return console.log(create);
  } catch (e) {
    return console.log(e);
  }
};

export const getProofTemplatesBySku = async (sku: string) => {
  try {
    const response = await api.get(`/catalog/templates/sku/${sku}`);
    return response.data.Items;
  } catch (e) {
    return console.log(e);
  }
};

export const deleteProofTemplate = async (id: string) => {
  try {
    let remove = await api.delete(`/catalog/templates/${id}`);
    return console.log(remove);
  } catch (e) {
    console.log(e);
  }
};

export const getCatalogProductPricesBySku = async (sku: string) => {
  try {
    try {
      const response = await api.get(`/catalog/prices/sku/${sku}`);
      return response.data.Items.sort(
        (a: PriceType, b: PriceType) => a.position - b.position
      );
    } catch (e) {
      console.log(e);
    }
  } catch (e) {
    console.log(e);
  }
};

export const createCatalogProductPrice = async (price: PriceType) => {
  try {
    const response = await api.post(`/catalog/prices`, price);
    console.log("createCatalogProductPrice", response);
    return response.status;
  } catch (e) {
    console.log(e);
  }
};

export const removeCatalogProductPrice = async (id: string) => {
  try {
    const response = await api.delete(`/catalog/prices/${id}`);
    return response;
  } catch (e) {
    console.log(e);
  }
};

type VariantColor = {
  color_name: string;
  color_hex: string;
};

type DesignRequest = {
  status: string;
  campaign: string;
  design_file_url: string;
  design_file_name: string;
  design_file_id: string;
  product_template_name: string;
  product_template_url: string;
  product_template_brand: string;
  product_template_model: string;
  product_template_sku: string;
  configured: boolean;
  configured_variants: VariantColor[];
  configured_layers: string[];
  configured_design_area: string;
  configured_athletes: string[];
  configured_organizations: string[];
  parent_organizations: string[];
  configured_data_url: string;
  request_priority: number;
  request_job_url: string;
  id: string;
  request_id: string;
  pk: string;
  updated_at: string;
  created_at: string;
};

export const createDesignRequest = async (
  request: Omit<
    DesignRequest,
    | "created_at"
    | "updated_at"
    | "id"
    | "pk"
    | "configured_data_url"
    | "request_id"
    | "request_job_url"
  >
) => {
  try {
    const response = await api.post("/designs", request);
    return response.data.id;
  } catch (e) {
    return null;
  }
};

export const restartDesignRequest = async (requestId: string) => {
  try {
    console.log("restart design id", requestId);
    const deletion = await api.delete(`designs/images/${requestId}`);
    console.log("design image delete", deletion);
    const response = await api.put(`designs/${requestId}`, {
      status: "start",
    });
    return first(response?.data?.Items);
  } catch (e) {
    return console.log(e);
  }
};

export const getAllDesignRequests = async () => {
  try {
    const response = await api.get(`/designs`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export type DesignProductTemplate = {
  id: string;
  created_at: string;
  updated_at: string;
  handle: string;
  title: string;
  body_html: string;
  campaign: string;
  organization: string;
  file: string;
  sku: string;
  variants: string[];
  layers: string[];
  design_area: string;
  stores: string[];
  licensing: string[];
  college_names: string[];
  org_names: string[];
  org_salesforce_ids: string[];
  tags: string[];
  upis: string[];
  auto_run: boolean;
  template_count: number;
  disabled: boolean;
};

export const createDesignProductTemplate = async (
  template: DesignProductTemplate,
  template_count: number
) => {
  try {
    const response = await api.post(`/product-templates`, {
      ...template,
      template_count: template_count,
    });
    console.log("post product-templates response", response);
    return response;
  } catch (e) {
    console.log(e);
  }
};

export const getAllDesignProductTemplates = async () => {
  try {
    const response = await api.get(`/product-templates`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const deleteDesignProductTemplate = async (id: string) => {
  try {
    const response = await api.delete(`/product-templates/${id}`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const disableDesignProductTemplate = async (
  id: string,
  disabled: boolean
) => {
  try {
    const response = await api.put(`/product-templates/${id}`, {
      disabled: disabled,
      updated_at: new Date().getTime().toString(),
    });
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export async function getDesignFilesByCampaignId(
  campaign: string,
  next?: string
) {
  try {
    const response = await api.get(`design-files/campaign/${campaign}`);
    return response.data.Items.filter((x: any) => x.id !== undefined)
      .sort((a: any, b: any) => Number(a.created_at) - Number(b.created_at))
      .reverse();
  } catch (e) {
    return [];
  }
}

export async function getDesignedProductTemplatesByCampaignId(
  campaign: string,
  next?: { createdAt: string; id: string }
) {
  try {
    let response;
    if (!next) {
      response = await api.get(`product-templates/campaign/${campaign}`);
    } else {
      response = await api.get(
        `product-templates/campaign/${campaign}/${campaign}?LastEvaluatedKey=${next.id}&createdAt=${next.createdAt}`
      );
    }
    const data = response.data;
    const last = data.LastEvaluatedKey ? data.LastEvaluatedKey.id : null;
    const lastCreated = data.LastEvaluatedKey
      ? data.LastEvaluatedKey.created_at
      : null;
    return {
      next: last,
      createdAt: lastCreated,
      items: data.Items ? data.Items : [],
    };
  } catch (e) {
    return {
      next: null,
      createdAt: null,
      items: [],
    };
  }
}

export const getDesignRequestsByCampaignId = async (
  campaign: string,
  next?: { createdAt: string; id: string }
) => {
  try {
    console.log(next);
    let response;
    if (!next) {
      response = await api.get(`/designs/campaign/${campaign}`);
    } else {
      response = await api.get(
        `/designs/campaign/${campaign}/?LastEvaluatedKey=${next.id}&createdAt=${next.createdAt}`
      );
    }
    console.log("response", response.data);
    const data = response.data;
    const last = data.LastEvaluatedKey ? data.LastEvaluatedKey.id : null;
    const lastCreated = data.LastEvaluatedKey
      ? data.LastEvaluatedKey.created_at
      : null;
    return {
      next: last,
      createdAt: lastCreated,
      items: data.Items ? data.Items : [],
    };
  } catch (e) {
    return {
      next: null,
      createdAt: null,
      items: [],
    };
  }
};

export async function getDesignRequest(
  requestId: string
): Promise<DesignRequest | null> {
  try {
    const response = await api.get(`/designs/${requestId}`);
    const items = response.data.Items;
    if (!items.length) {
      return null;
    }
    return first(items) as DesignRequest;
  } catch (e) {
    return null;
  }
}

export const getRequestLogByRequestId = async (requestId: string) => {
  try {
    const response = await api.get(`/designs/log/${requestId}`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const getPrintFilesByDesignRequestId = async (requestId: string) => {
  try {
    const response = await api.get(`/print-files/${requestId}`);
    return response.data.Items.filter((x: any) => x.file_type === "png");
  } catch (e) {
    console.log(e);
  }
};

export const deletePrintFilesByDesignRequestId = async (requestId: string) => {
  try {
    const response = await api.delete(`/print-files/${requestId}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const getGalleryImagesByDesignRequestId = async (requestId: string) => {
  try {
    const response = await api.get(`/gallery-images/${requestId}`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const deleteGalleryImagesByDesignRequestId = async (
  requestId: string
) => {
  try {
    const response = await api.delete(`/gallery-images/${requestId}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const getProofImagesByDesignRequestId = async (requestId: string) => {
  try {
    const response = await api.get(`/proof-images/${requestId}`);
    return response.data.Items;
  } catch (e) {
    console.log(e);
  }
};

export const deleteProofImagesByDesignRequestId = async (requestId: string) => {
  try {
    const response = await api.delete(`/proof-images/${requestId}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};

export const deleteDesignRequestId = async (requestId: string) => {
  try {
    await deletePrintFilesByDesignRequestId(requestId);
    await deleteGalleryImagesByDesignRequestId(requestId);
    await deleteProofImagesByDesignRequestId(requestId);
    const response = await api.delete(`/designs/${requestId}`);
    return response.data;
  } catch (e) {
    console.log(e);
  }
};
